// Settings → Payments (docs/21 §8) — agency-admin configures the per-agency payment rail.
// GET/PUT /billing/payment-config. "nsofar" calls nsofar's API to mint links (no local creds);
// "razorpay"/"manual" are the agency's own rail (link generation lands in Phase 2). Razorpay
// credentials are write-only here: entered to save, never returned (shown masked as ••last4).

import { useEffect, useState } from "react";
import type { JSX, FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch } from "../lib/api.js";
import { useAuth } from "../hooks/useAuth.js";
import { Shell } from "../components/Shell.js";
import { FormField } from "../components/FormField.js";

type Provider = "nsofar" | "razorpay" | "manual";

interface PaymentConfig {
  configured: boolean;
  provider: Provider | null;
  default_currency: string;
  is_active: boolean;
  has_razorpay_credentials: boolean;
  key_id_last4: string | null;
}

const SEL_CLS = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-navy-500";
const PROVIDERS: { value: Provider; label: string; hint: string }[] = [
  { value: "nsofar",   label: "nsofar (Stripe via nsofar)", hint: "HCM asks nsofar to mint the Stripe payment link. No local credentials." },
  { value: "razorpay", label: "Razorpay (your own account)", hint: "HCM generates links with your Razorpay credentials (link generation: Phase 2)." },
  { value: "manual",   label: "Manual (paste your own link)", hint: "You generate the link yourself; HCM records it with nsofar (Phase 2)." },
];

function StatusCard({ cfg }: { cfg: PaymentConfig }): JSX.Element {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 space-y-1">
      <div>Current provider: <span className="font-medium text-slate-900">{cfg.provider ?? "not configured"}</span></div>
      <div>Default currency: <span className="font-medium text-slate-900">{cfg.default_currency}</span></div>
      {cfg.has_razorpay_credentials && (
        <div>Razorpay key: <span className="font-mono text-slate-900">••••{cfg.key_id_last4}</span> (stored encrypted)</div>
      )}
    </div>
  );
}

function RazorpayFields({
  keyId, setKeyId, keySecret, setKeySecret, webhookSecret, setWebhookSecret,
}: {
  keyId: string; setKeyId: (v: string) => void;
  keySecret: string; setKeySecret: (v: string) => void;
  webhookSecret: string; setWebhookSecret: (v: string) => void;
}): JSX.Element {
  return (
    <div className="space-y-4 rounded-lg border border-slate-200 p-4">
      <p className="text-xs text-slate-500">Credentials are write-only — re-enter them to save Razorpay settings. They are encrypted at rest and never displayed again.</p>
      <FormField label="Razorpay Key ID" value={keyId} onChange={setKeyId} placeholder="rzp_live_…" />
      <FormField label="Razorpay Key Secret" type="password" value={keySecret} onChange={setKeySecret} placeholder="••••••••" />
      <FormField label="Razorpay Webhook Secret (optional)" type="password" value={webhookSecret} onChange={setWebhookSecret} placeholder="whsec_…" />
    </div>
  );
}

interface ConfigForm {
  provider: Provider; currency: string;
  keyId: string; keySecret: string; webhookSecret: string;
}

function buildPayload(f: ConfigForm): Record<string, string> {
  const payload: Record<string, string> = { provider: f.provider };
  if (f.currency) payload["default_currency"] = f.currency.toUpperCase();
  if (f.provider === "razorpay") {
    payload["razorpay_key_id"] = f.keyId.trim();
    payload["razorpay_key_secret"] = f.keySecret.trim();
    if (f.webhookSecret.trim()) payload["razorpay_webhook_secret"] = f.webhookSecret.trim();
  }
  return payload;
}

async function saveConfig(
  f: ConfigForm,
  setSaving: (b: boolean) => void,
  setApiError: (e: string | null) => void,
  onSaved: () => void,
): Promise<void> {
  setApiError(null);
  if (f.provider === "razorpay" && (!f.keyId.trim() || !f.keySecret.trim())) {
    setApiError("Razorpay requires a Key ID and Key Secret."); return;
  }
  setSaving(true);
  try {
    await apiFetch("/billing/payment-config", { method: "PUT", body: JSON.stringify(buildPayload(f)) });
    toast.success("Payment settings saved");
    onSaved();
  } catch (err) {
    setApiError(err instanceof Error ? err.message : "Failed to save payment settings.");
  } finally { setSaving(false); }
}

function PaymentConfigForm({ cfg, onSaved }: { cfg: PaymentConfig; onSaved: () => void }): JSX.Element {
  const [provider, setProvider] = useState<Provider>(cfg.provider ?? "nsofar");
  const [currency, setCurrency] = useState(cfg.default_currency || "USD");
  const [keyId, setKeyId] = useState("");
  const [keySecret, setKeySecret] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [apiError, setApiError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const hint = PROVIDERS.find((p) => p.value === provider)?.hint ?? "";

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    void saveConfig({ provider, currency, keyId, keySecret, webhookSecret }, setSaving, setApiError, onSaved);
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div>
        <label htmlFor="pc-provider" className="block text-sm font-medium text-slate-700 mb-1">Payment provider</label>
        <select id="pc-provider" value={provider} onChange={(e) => setProvider(e.target.value as Provider)} className={SEL_CLS}>
          {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        <p className="mt-1 text-xs text-slate-500">{hint}</p>
      </div>
      <div className="w-40">
        <FormField label="Default currency" value={currency} onChange={setCurrency} placeholder="USD" />
      </div>
      {provider === "razorpay" && (
        <RazorpayFields keyId={keyId} setKeyId={setKeyId} keySecret={keySecret} setKeySecret={setKeySecret}
          webhookSecret={webhookSecret} setWebhookSecret={setWebhookSecret} />
      )}
      {apiError && <p role="alert" className="text-sm text-red-600">{apiError}</p>}
      <div className="flex justify-end">
        <button type="submit" disabled={saving}
          className="px-4 py-2 text-sm rounded-lg bg-navy-700 text-white font-medium hover:bg-navy-800 disabled:opacity-50">
          {saving ? "Saving…" : "Save settings"}
        </button>
      </div>
    </form>
  );
}

const DEFAULT_CFG: PaymentConfig = {
  configured: false, provider: null, default_currency: "USD", is_active: false,
  has_razorpay_credentials: false, key_id_last4: null,
};

export function PaymentConfigPage(): JSX.Element {
  const { user } = useAuth();
  const { data, isLoading, refetch } = useQuery<PaymentConfig>({
    queryKey: ["payment-config"],
    queryFn: () => apiFetch<PaymentConfig>("/billing/payment-config"),
    enabled: user?.role === "agency_admin",
  });
  // Remount the form when the loaded provider changes so its initial state reflects the server.
  const [formKey, setFormKey] = useState(0);
  useEffect(() => { setFormKey((k) => k + 1); }, [data?.provider]);

  if (user?.role !== "agency_admin") {
    return <Shell><div className="max-w-3xl mx-auto px-4 py-6 text-slate-600">Only agency admins can manage payment settings.</div></Shell>;
  }

  const cfg = data ?? DEFAULT_CFG;
  return (
    <Shell>
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Payment settings</h1>
          <p className="text-sm text-slate-500 mt-1">Choose how this agency issues payment links to families.</p>
        </div>
        {isLoading ? <p className="text-slate-500">Loading…</p> : (
          <>
            <StatusCard cfg={cfg} />
            <PaymentConfigForm key={formKey} cfg={cfg} onSaved={() => void refetch()} />
          </>
        )}
      </div>
    </Shell>
  );
}
